import { useCallback, useEffect, useState } from "react";

export function useResolvedById<T>(
  value: number | null,
  fetcher: (id: number, signal: AbortSignal) => Promise<T>,
): [T | null, (id: number, dto: T) => void, unknown] {
  const [cache, setCache] = useState<Map<number, T>>(() => new Map());
  const [error, setError] = useState<unknown>(null);

  const resolved = value !== null ? (cache.get(value) ?? null) : null;

  useEffect(() => {
    if (value === null) return;
    if (cache.has(value)) return;

    const controller = new AbortController();
    fetcher(value, controller.signal)
      .then((dto) => {
        if (controller.signal.aborted) return;
        setCache((prev) => {
          if (prev.has(value)) return prev;
          return new Map(prev).set(value, dto);
        });
        setError(null);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err);
      });

    return () => {
      controller.abort();
    };
  }, [value, cache, fetcher]);

  const preload = useCallback((id: number, dto: T) => {
    setCache((prev) => {
      if (prev.has(id)) return prev;
      return new Map(prev).set(id, dto);
    });
  }, []);

  return [resolved, preload, error];
}
